import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import pixService from "@/services/pixService";
import { printOrder } from "@/services/printService";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Separator } from "./ui/separator";
import {
  Package,
  ShoppingCart,
  Clock,
  CheckCircle,
  Truck,
  Ticket,
  Phone,
  Printer,
  X,
  Eye,
  Wallet,
  ShoppingBag,
  User,
  DollarSign,
  MapPin,
  Copy,
} from "lucide-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";
const API = `${BACKEND_URL}`;

// Helper function para normalizar endereço
const normalizeAddress = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return { description: value };
    }
  }
  return null;
};

// Modal de Detalhes do Pedido
const OrderDetailsModal = ({
  open,
  onOpenChange,
  order,
  onPrintOrder,
  showTextModal,
  setShowTextModal,
  receiptText,
  setReceiptText,
}) => {
  if (!order) return null;

  const calculateOrderTotal = () => {
    let productsTotal = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        productsTotal += parseFloat(product.total_value || 0);
      });
    }
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    const discountAmount = productsTotal * (discountPercentage / 100);
    const subtotalWithDiscount = productsTotal - discountAmount;
    const freightValue = parseFloat(order.freight_value || 0);
    return subtotalWithDiscount + freightValue;
  };

  const getProductsSubtotal = () => {
    let total = 0;
    if (order.products && Array.isArray(order.products)) {
      order.products.forEach((product) => {
        total += parseFloat(product.total_value || 0);
      });
    }
    return total;
  };

  const getDiscountAmount = () => {
    const productsTotal = getProductsSubtotal();
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    return productsTotal * (discountPercentage / 100);
  };

  const generateReceiptText = () => {
    const lineWidth = 32;
    const separator = "=".repeat(lineWidth);
    const dashedLine = "-".repeat(lineWidth);

    const removeAccents = (text) => {
      const map = {
        á: "a",
        à: "a",
        ã: "a",
        â: "a",
        ä: "a",
        é: "e",
        è: "e",
        ê: "e",
        ë: "e",
        í: "i",
        ì: "i",
        î: "i",
        ï: "i",
        ó: "o",
        ò: "o",
        õ: "o",
        ô: "o",
        ö: "o",
        ú: "u",
        ù: "u",
        û: "u",
        ü: "u",
        ç: "c",
        ñ: "n",
        Á: "A",
        À: "A",
        Ã: "A",
        Â: "A",
        Ä: "A",
        É: "E",
        È: "E",
        Ê: "E",
        Ë: "E",
        Í: "I",
        Ì: "I",
        Î: "I",
        Ï: "I",
        Ó: "O",
        Ò: "O",
        Õ: "O",
        Ô: "O",
        Ö: "O",
        Ú: "U",
        Ù: "U",
        Û: "U",
        Ü: "U",
        Ç: "C",
        Ñ: "N",
      };
      return text
        .split("")
        .map((char) => map[char] || char)
        .join("");
    };

    const centerText = (text) => {
      const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
      return " ".repeat(padding) + text;
    };

    const formatDate = (dateString) => {
      if (!dateString) return new Date().toLocaleString("pt-BR");
      return new Date(dateString).toLocaleString("pt-BR");
    };

    const productsSubtotal = getProductsSubtotal();
    const discountAmount = getDiscountAmount();
    const total = calculateOrderTotal();

    let text = "";
    text += centerText("BONFIM GOURMET") + "\n";
    text += centerText("Comanda de Pedido") + "\n";
    text += separator + "\n";
    text += `Pedido #${order.code}\n`;
    text += `Data ${formatDate(order.created_at)}\n`;
    text += `Status ${removeAccents(order.status || "N/A")}\n`;
    text += separator + "\n";

    text += "\nCLIENTE\n";
    const clientName = removeAccents(order.client?.name || "Anonimo");
    text += clientName + "\n";
    text += `Tel ${order.client?.contact || "N/A"}\n`;
    text += separator + "\n";

    text += "\nITENS DO PEDIDO\n";
    text += dashedLine + "\n";

    order.products?.forEach((product, idx) => {
      const prodName = removeAccents(product.product?.name || "Produto");
      text += `\n${idx + 1}. ${prodName} | Qtd ${product.quantity}\n`;
      text += `   Tot R$ ${parseFloat(product.total_value).toFixed(2)}\n`;

      if (product.observation) {
        text += `   Obs: ${removeAccents(product.observation)}\n`;
      }
      text += dashedLine + "\n";
    });

    text += "\n" + separator + "\n";
    text += `Subtotal R$ ${productsSubtotal.toFixed(2)}\n`;

    if (order.coupon_code) {
      text += `Desconto ${order.coupon_code}\n`;
      text += `${parseFloat(order.discount_percentage || 0).toFixed(
        1
      )}% -R$ ${discountAmount.toFixed(2)}\n`;
    }

    text += separator + "\n";
    text += `TOTAL R$ ${total.toFixed(2)}\n`;
    text += separator + "\n";
    text += `\nPagamento ${removeAccents(order.pay_method || "N/A")}\n`;
    text += `Entrega ${removeAccents(order.delivery_type || "N/A")}\n`;
    if (order.payed) {
      text += "Status PAGO\n";
    }
    text += separator + "\n";
    text += "\n" + centerText("Obrigado!") + "\n";
    text += centerText("BONFIM GOURMET") + "\n\n";

    return text;
  };

  const handleCopyText = () => {
    const text = generateReceiptText();
    setReceiptText(text);
    setShowTextModal(true);
  };

  const copyToClipboard = async () => {
    try {
      const removeAccents = (text) => {
        const map = {
          á: "a",
          à: "a",
          ã: "a",
          â: "a",
          ä: "a",
          é: "e",
          è: "e",
          ê: "e",
          ë: "e",
          í: "i",
          ì: "i",
          î: "i",
          ï: "i",
          ó: "o",
          ò: "o",
          õ: "o",
          ô: "o",
          ö: "o",
          ú: "u",
          ù: "u",
          û: "u",
          ü: "u",
          ç: "c",
          ñ: "n",
          Á: "A",
          À: "A",
          Ã: "A",
          Â: "A",
          Ä: "A",
          É: "E",
          È: "E",
          Ê: "E",
          Ë: "E",
          Í: "I",
          Ì: "I",
          Î: "I",
          Ï: "I",
          Ó: "O",
          Ò: "O",
          Õ: "O",
          Ô: "O",
          Ö: "O",
          Ú: "U",
          Ù: "U",
          Û: "U",
          Ü: "U",
          Ç: "C",
          Ñ: "N",
        };
        return text
          .split("")
          .map((char) => map[char] || char)
          .join("");
      };

      const textWithoutAccents = removeAccents(receiptText);

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textWithoutAccents);
        alert("✅ Texto copiado para a área de transferência!");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textWithoutAccents;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        textArea.remove();
        if (successful) {
          alert("✅ Texto copiado para a área de transferência!");
        } else {
          throw new Error("execCommand falhou");
        }
      }
    } catch (err) {
      console.error("Erro ao copiar:", err);
      alert("⚠️ Não foi possível copiar automaticamente.");
    }
  };

  const hasFreight =
    order.delivery_type?.toLowerCase() === "delivery" &&
    parseFloat(order.freight_value || 0) > 0;

  const isDelivery = order.delivery_type?.toLowerCase() === "delivery";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg md:text-xl">
              <ShoppingBag className="h-5 w-5 md:h-6 md:w-6 text-[#c9332C]" />
              Pedido #{order.code}
              {order.payed === true && (
                <Badge className="text-xs md:text-sm bg-green-600 text-white border-0 shadow-sm ml-2">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Pago
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm mt-2 px-6">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <strong>{order.client?.name || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <strong>{order.client?.contact || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <strong>{order.pay_method || "N/A"}</strong>
            </div>
            <div className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              <strong>{order.delivery_type || "N/A"}</strong>
            </div>
          </div>

          <Separator className="my-4" />

          <div className="space-y-4">
            {/* Produtos do Pedido */}
            {order.products && order.products.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-base md:text-lg flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Itens do Pedido
                </h3>
                {order.products.map((product, productIdx) => (
                  <div
                    key={productIdx}
                    className="border rounded-lg p-3 md:p-4 bg-gray-50 space-y-3"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            #{productIdx + 1}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Qtd: {product.quantity}
                          </Badge>
                        </div>
                        {product.product && (
                          <div>
                            <p className="font-bold text-gray-900 text-sm md:text-base">
                              {product.product.name}
                            </p>
                            {product.product.description && (
                              <p className="text-xs text-gray-600">
                                {product.product.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <Badge className="bg-[#c9332C] text-white whitespace-nowrap text-xs md:text-sm">
                        R$ {parseFloat(product.total_value).toFixed(2)}
                      </Badge>
                    </div>

                    {product.observation && (
                      <div className="bg-yellow-50 p-2 md:p-3 rounded border border-yellow-200">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">
                          Observação:
                        </p>
                        <p className="text-xs md:text-sm text-yellow-900">
                          {product.observation}
                        </p>
                      </div>
                    )}

                    {product.selected_produtos &&
                      product.selected_produtos.length > 0 && (
                        <div className="bg-blue-50 p-2 md:p-3 rounded border border-blue-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-blue-900">
                            Produtos Selecionados
                          </p>
                          {product.selected_produtos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-blue-100 last:border-b-0"
                            >
                              <span className="text-blue-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-blue-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                    {product.selected_ingredients &&
                      product.selected_ingredients.length > 0 && (
                        <div className="bg-amber-50 p-2 md:p-3 rounded border border-amber-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-amber-900">
                            Ingredientes Inclusos
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selected_ingredients.map((ing, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between text-xs md:text-sm text-amber-900"
                              >
                                <span>• {ing.name}</span>
                                <span className="font-semibold">
                                  +R$ {(ing.price || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraIngredients &&
                      product.selectedExtraIngredients.length > 0 && (
                        <div className="bg-green-50 p-2 md:p-3 rounded border border-green-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-green-900">
                            Ingredientes Extras
                          </p>
                          <div className="pl-3 space-y-1">
                            {product.selectedExtraIngredients.map(
                              (ing, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between text-xs md:text-sm text-green-900"
                                >
                                  <span>• {ing.name}</span>
                                  <span className="font-semibold">
                                    +R$ {(ing.price || 0).toFixed(2)}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {product.selectedExtraProdutos &&
                      product.selectedExtraProdutos.length > 0 && (
                        <div className="bg-purple-50 p-2 md:p-3 rounded border border-purple-200 space-y-1">
                          <p className="text-xs md:text-sm font-semibold text-purple-900">
                            Produtos Extras
                          </p>
                          {product.selectedExtraProdutos.map((prod, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center text-xs md:text-sm pl-3 pb-1 border-b border-purple-100 last:border-b-0"
                            >
                              <span className="text-purple-900">
                                {prod.name}{" "}
                                <span className="text-gray-500">
                                  x{prod.qty}
                                </span>
                              </span>
                              <span className="font-semibold text-purple-900">
                                R$ {(prod.price * prod.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            )}

            {/* Total Geral */}
            <Separator />

            {hasFreight && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-500 shadow-sm">
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-orange-800 mb-2">
                      Taxa de Entrega Incluída
                    </p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Subtotal produtos:
                        </span>
                        <span className="font-semibold text-orange-900">
                          R$ {getProductsSubtotal().toFixed(2)}
                        </span>
                      </div>
                      {order.coupon_code && (
                        <div className="flex justify-between text-sm items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-purple-700">Desconto:</span>
                            <Badge className="text-xs bg-purple-600 text-white border-0">
                              <Ticket className="h-3 w-3 mr-1" />
                              {order.coupon_code} (
                              {parseFloat(
                                order.discount_percentage || 0
                              ).toFixed(2)}
                              %)
                            </Badge>
                          </div>
                          <span className="font-semibold text-purple-700">
                            - R$ {getDiscountAmount().toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-700">
                          Taxa de delivery:
                        </span>
                        <span className="font-bold text-orange-900">
                          R$ {parseFloat(order.freight_value || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-emerald-50 p-3 md:p-4 rounded-lg border-2 border-green-300">
              <span className="text-sm md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                Total {hasFreight ? "(com taxa)" : ""}:
              </span>
              <span className="text-xl md:text-3xl font-bold text-green-600">
                R$ {calculateOrderTotal().toFixed(2)}
              </span>
            </div>

            {/* Botões de Impressão */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => onPrintOrder && onPrintOrder(order)}
                className="w-full bg-gradient-to-r from-[#c9332C] to-[#a02820] hover:from-[#a02820] hover:to-[#8a2118] text-white flex items-center justify-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Imprimir
              </Button>
              <Button
                onClick={handleCopyText}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" />
                Copiar Texto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Texto para Copiar */}
      <Dialog open={showTextModal} onOpenChange={setShowTextModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Texto do Pedido #{order.code}
            </DialogTitle>
            <DialogDescription>
              Copie o texto abaixo e cole no software de impressão térmica
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800 font-medium mb-1">
                ℹ️ Texto otimizado para impressora térmica
              </p>
              <p className="text-blue-700 text-xs">
                O texto está sem acentos para garantir compatibilidade com
                impressoras térmicas.
              </p>
            </div>
            <textarea
              value={receiptText}
              readOnly
              className="w-full h-96 p-4 font-mono text-xs border rounded-lg bg-gray-50 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                fontFamily: "'Courier New', monospace",
                whiteSpace: "pre",
              }}
            />
            <div className="flex gap-3">
              <Button
                onClick={copyToClipboard}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar para Área de Transferência
              </Button>
              <Button
                onClick={() => setShowTextModal(false)}
                variant="outline"
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Modal de Confirmação de Pagamento
const PaymentConfirmationModal = ({
  open,
  onOpenChange,
  order,
  onConfirm,
  loading,
}) => {
  const [paymentData, setPaymentData] = useState({
    pay_method: "",
    value_received: "",
    name: "",
    contact: "",
  });

  const paymentMethods = [
    "PIX",
    "Dinheiro",
    "Cartão de Crédito",
    "Cartão de Débito",
    "Vale Refeição",
    "Transferência Bancária",
  ];

  useEffect(() => {
    if (open && order) {
      const productsTotal =
        order.products?.reduce(
          (sum, product) => sum + parseFloat(product.total_value || 0),
          0
        ) || 0;
      const freightValue = parseFloat(order.freight_value || 0);
      const discountPercentage = parseFloat(order.discount_percentage || 0);
      const discountAmount = productsTotal * (discountPercentage / 100);
      const orderTotal = productsTotal - discountAmount + freightValue;

      setPaymentData({
        pay_method: order.pay_method || "",
        value_received: orderTotal.toFixed(2),
        name: order.client?.name || "",
        contact: order.client?.contact || "",
      });
    }
  }, [open, order]);

  const handleConfirm = () => {
    if (!paymentData.pay_method || !paymentData.value_received) {
      alert("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    onConfirm({
      ...paymentData,
      amount: parseFloat(paymentData.value_received),
      value_received: parseFloat(paymentData.value_received),
      orderCode: order.code,
    });
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5 text-[#c9332C]" />
            Confirmar Pagamento
          </DialogTitle>
          <DialogDescription className="text-sm">
            Pedido <strong>#{order.code}</strong> - {order.client?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Método de Pagamento */}
          <div className="space-y-2">
            <Label
              htmlFor="payment-method"
              className="text-sm font-semibold text-gray-700"
            >
              Método de Pagamento <span className="text-red-500">*</span>
            </Label>
            <Select
              value={paymentData.pay_method}
              onValueChange={(value) =>
                setPaymentData({ ...paymentData, pay_method: value })
              }
            >
              <SelectTrigger id="payment-method" className="h-11">
                <SelectValue placeholder="Selecione o método de pagamento" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method} value={method}>
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Valor Recebido */}
          <div className="space-y-2">
            <Label
              htmlFor="value-received"
              className="text-sm font-semibold text-gray-700"
            >
              Valor Recebido (R$) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="value-received"
              type="number"
              step="0.01"
              min="0"
              value={paymentData.value_received}
              onChange={(e) =>
                setPaymentData({
                  ...paymentData,
                  value_received: e.target.value,
                })
              }
              className="h-11"
              placeholder="0.00"
            />
          </div>

          {/* Nome do Cliente */}
          <div className="space-y-2">
            <Label
              htmlFor="client-name"
              className="text-sm font-semibold text-gray-700"
            >
              Nome do Cliente
            </Label>
            <Input
              id="client-name"
              type="text"
              value={paymentData.name}
              onChange={(e) =>
                setPaymentData({ ...paymentData, name: e.target.value })
              }
              className="h-11"
              placeholder="Nome do cliente"
            />
          </div>

          {/* Contato */}
          <div className="space-y-2">
            <Label
              htmlFor="contact"
              className="text-sm font-semibold text-gray-700"
            >
              Contato
            </Label>
            <Input
              id="contact"
              type="text"
              value={paymentData.contact}
              onChange={(e) =>
                setPaymentData({ ...paymentData, contact: e.target.value })
              }
              className="h-11"
              placeholder="Telefone ou e-mail"
            />
          </div>

          {/* Resumo */}
          <div className="bg-gray-50 p-4 rounded-lg border space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase">
              Resumo
            </p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pedido:</span>
                <span className="font-semibold">#{order.code}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pagamento:</span>
                <span className="font-semibold">
                  {paymentData.pay_method || "-"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-bold text-[#c9332C]">
                  R$ {paymentData.value_received || "0.00"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              loading || !paymentData.pay_method || !paymentData.value_received
            }
            className="flex-1 bg-[#c9332C] hover:bg-[#a02820]"
          >
            {loading ? "Confirmando..." : "Confirmar Pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [receiptText, setReceiptText] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [loading, setLoading] = useState(false);

  // Buscar pedidos ativos (não finalizados e não cancelados)
  const fetchActiveOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API}/orders/admin?sort=desc`);
      const allOrders = Array.isArray(response.data.data)
        ? response.data.data
        : [];

      // Filtrar apenas pedidos ativos (não finalizados e não cancelados)
      const activeOrders = allOrders.filter(
        (order) => order.status !== "Finalizado" && order.status !== "Cancelado"
      );

      setOrders(activeOrders);
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // Atualizar status do pedido
  const handleUpdateStatus = async (orderCode, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderCode}/status`,
        { status: newStatus },
        { headers: { "Content-Type": "application/json" } }
      );

      // Se o status for Finalizado ou Cancelado, remover da lista
      if (newStatus === "Finalizado" || newStatus === "Cancelado") {
        setOrders((prevOrders) =>
          prevOrders.filter((order) => order.code !== orderCode)
        );
      } else {
        // Caso contrário, atualizar o status na lista
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.code === orderCode ? { ...order, status: newStatus } : order
          )
        );
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      alert("Erro ao atualizar o status do pedido");
    }
  };

  // WhatsApp
  const handleWhatsApp = (order) => {
    const phone = order.client?.phone || order.client_phone || "";
    const cleanPhone = phone.replace(/\D/g, "");
    if (!cleanPhone) {
      alert("Telefone não disponível");
      return;
    }
    const message = `Olá! Seu pedido #${
      order.code
    } está ${order.status.toLowerCase()}.`;
    window.open(
      `https://wa.me/55${cleanPhone}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  // Imprimir pedido
  // Visualizar detalhes do pedido
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  // Confirmar pagamento
  const handleConfirmPayment = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  // Processar confirmação de pagamento
  const handlePaymentConfirm = async (paymentData) => {
    setLoading(true);
    try {
      await pixService.confirmPayment(paymentData.orderCode, paymentData);
      setPaymentModalOpen(false);
      alert("✅ Pagamento confirmado com sucesso!");
      // Recarregar pedidos
      await fetchActiveOrders();
    } catch (error) {
      console.error("Erro ao confirmar pagamento:", error);
      alert("❌ Erro ao confirmar pagamento. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveOrders();
  }, [fetchActiveOrders]);

  // Função para obter cor do status
  const getStatusColor = (status) => {
    const colors = {
      Fechado: "bg-red-500",
      Recebido: "bg-blue-500",
      Preparando: "bg-yellow-500",
      Liberado: "bg-purple-500",
      Entregue: "bg-teal-500",
    };
    return colors[status] || "bg-gray-500";
  };

  // Calcular total do pedido
  const calculateTotal = (order) => {
    const productsTotal =
      order.products?.reduce(
        (sum, product) => sum + parseFloat(product.total_value || 0),
        0
      ) || 0;
    const discountPercentage = parseFloat(order.discount_percentage || 0);
    const discountAmount = productsTotal * (discountPercentage / 100);
    const freightValue = parseFloat(order.freight_value || 0);
    return productsTotal - discountAmount + freightValue;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">
          Dashboard - Pedidos Ativos
        </h2>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {orders.length} pedido{orders.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Seção de Pedidos Ativos */}
      <div className="mt-8">
        {loadingOrders ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Carregando pedidos...</p>
          </div>
        ) : orders.length === 0 ? (
          <Card className="bg-gray-50">
            <CardContent className="p-8 text-center">
              <ShoppingCart className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">
                Nenhum pedido ativo no momento
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {orders.map((order) => (
              <Card
                key={order.code}
                className={`overflow-hidden hover:shadow-xl transition-all duration-300 border-t-4 ${getStatusColor(
                  order.status
                )}`}
              >
                <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="font-bold border-[#c9332C] text-[#c9332C]"
                        >
                          #{order.code}
                        </Badge>
                        {order.payed === true && (
                          <Badge className="bg-green-600 text-white text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Pago
                          </Badge>
                        )}
                      </div>
                      <Badge
                        className={`${getStatusColor(
                          order.status
                        )} text-white text-sm px-3 py-1`}
                      >
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {/* Cliente */}
                  <div>
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {order.client?.name || "Cliente não informado"}
                    </p>
                    {order.client?.phone && (
                      <p className="text-xs text-gray-600">
                        {order.client.phone}
                      </p>
                    )}
                  </div>

                  {/* Informações */}
                  <div className="space-y-1 text-xs text-gray-600">
                    {order.date_time && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{order.date_time}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      <span>{order.products?.length || 0} itens</span>
                    </div>
                    {order.delivery_type?.toLowerCase() === "delivery" &&
                      parseFloat(order.freight_value || 0) > 0 && (
                        <div className="flex items-center gap-1">
                          <Truck className="h-3 w-3" />
                          <span>
                            Taxa: R${" "}
                            {parseFloat(order.freight_value).toFixed(2)}
                          </span>
                        </div>
                      )}
                    {order.coupon_code && (
                      <div className="flex items-center gap-1">
                        <Ticket className="h-3 w-3" />
                        <span>{order.coupon_code}</span>
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Total:
                      </span>
                      <span className="text-lg font-bold text-[#c9332C]">
                        R$ {calculateTotal(order).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleViewDetails(order)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Detalhes
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleConfirmPayment(order)}
                    >
                      <Wallet className="h-3 w-3 mr-1" />
                      Pagamento
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleWhatsApp(order)}
                    >
                      <Phone className="h-3 w-3 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => printOrder(order)}
                    >
                      <Printer className="h-3 w-3 mr-1" />
                      Imprimir
                    </Button>
                  </div>

                  {/* Atualizar Status */}
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-xs font-medium text-gray-700">
                      Atualizar Status:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {order.status !== "Preparando" && (
                        <Button
                          size="sm"
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                          onClick={() =>
                            handleUpdateStatus(order.code, "Preparando")
                          }
                        >
                          Preparando
                        </Button>
                      )}
                      {order.status !== "Liberado" && (
                        <Button
                          size="sm"
                          className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                          onClick={() =>
                            handleUpdateStatus(order.code, "Liberado")
                          }
                        >
                          Liberado
                        </Button>
                      )}
                      {order.status !== "Entregue" && (
                        <Button
                          size="sm"
                          className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                          onClick={() =>
                            handleUpdateStatus(order.code, "Entregue")
                          }
                        >
                          Entregue
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() =>
                          handleUpdateStatus(order.code, "Finalizado")
                        }
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Finalizar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full"
                        onClick={() =>
                          handleUpdateStatus(order.code, "Cancelado")
                        }
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Pedido */}
      {selectedOrder && (
        <OrderDetailsModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          order={selectedOrder}
          onPrintOrder={printOrder}
          showTextModal={showTextModal}
          setShowTextModal={setShowTextModal}
          receiptText={receiptText}
          setReceiptText={setReceiptText}
        />
      )}

      {/* Modal de Pagamento */}
      {selectedOrderForPayment && (
        <PaymentConfirmationModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          order={selectedOrderForPayment}
          onConfirm={handlePaymentConfirm}
          loading={loading}
        />
      )}
    </div>
  );
};

export default Dashboard;
